import { IcoFileText } from "../../../../components/icons";
import { RatingInput } from "../../../../components/ui";
import { linkifyHtml } from "../../../../utils/linkify";
import type { Question } from "./types";

type FormPreviewProps = {
  title: string;
  questions: Question[];
};

export function FormPreview({ title, questions }: FormPreviewProps) {
  const activeQuestions = questions
    .filter((q) => !q.archived)
    .sort((a, b) => a.seq - b.seq);

  return (
    <div class="flex flex-col gap-6">
      {/* Header - matches submit page style */}
      <div class="flex items-start gap-4">
        <div class="flex items-center justify-center size-12 rounded-lg bg-surface-alt">
          <IcoFileText class="size-6 text-text-muted" />
        </div>
        <div class="flex-1">
          <h2 class="text-xl font-bold text-text">
            {title || "Untitled form"}
          </h2>
          <p class="text-xs text-text-muted mt-1">Preview of end-user view</p>
        </div>
      </div>

      {/* Questions */}
      {activeQuestions.length === 0 ? (
        <div class="rounded-xl border border-border bg-surface p-8 text-center">
          <p class="text-text-muted">No questions to preview yet.</p>
          <p class="text-sm text-text-subtle mt-1">
            Add questions to see how they'll appear to users.
          </p>
        </div>
      ) : (
        <div class="flex flex-col gap-4">
          {activeQuestions.map((question, index) => (
            <PreviewQuestionCard
              key={question.id}
              number={index + 1}
              question={question}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type PreviewQuestionCardProps = {
  number: number;
  question: Question;
};

function PreviewQuestionCard({ number, question }: PreviewQuestionCardProps) {
  return (
    <div class="rounded-xl border border-border bg-surface p-6">
      <div class="flex items-start gap-3 mb-4">
        <span class="flex items-center justify-center size-6 rounded-full bg-surface-muted text-xs font-medium text-text-muted">
          {number}
        </span>
        <div class="flex-1 min-w-0">
          {/* Title - render as HTML for rich text */}
          {question.title ? (
            <div
              class="font-medium text-text prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: question.title }}
            />
          ) : (
            <p class="font-medium text-text-muted italic">Untitled question</p>
          )}
          {/* Description if any */}
          {question.description && (
            <div
              class="text-sm text-text-muted mt-1 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: linkifyHtml(question.description),
              }}
            />
          )}
        </div>
      </div>

      {/* Answer input placeholder based on type */}
      {question.type === "short_answer" ? (
        <div class="rounded-lg border border-dashed border-border bg-surface-muted p-4">
          <p class="text-sm text-text-subtle italic">
            Text input will appear here
          </p>
        </div>
      ) : (
        <RatingInput
          max={question.ratingMax}
          value={null}
          onChange={() => {}}
          disabled
        />
      )}
    </div>
  );
}
